export interface ConsentClientOptions {
  publishableKey: string;
  baseUrl: string;
  language?: "es-CL" | "en-US";
}

export type IdentifierKind = "EMAIL" | "RUT" | "PHONE" | "COOKIE_ID" | "EXTERNAL_ID";

export type ConsentMethod =
  | "implicit"
  | "explicit_clickwrap"
  | "double_opt_in"
  | "signed_pdf"
  | "biometric_attested"
  | "notarized";

export interface RecordConsentInput {
  subject: {
    email?: string;
    rut?: string;
    phone?: string;
    cookieId?: string;
    externalId?: string;
  };
  purposeId: string;
  consentMethod: ConsentMethod;
  language?: "es-CL" | "en-US";
  context?: { pageUrl?: string; formId?: string };
  policyVersionId?: string;
}

export interface RecordConsentResponse {
  record: { id: string; status: string; given_at: string; expires_at: string | null };
  event: { id: string; sequence_number: number; record_hash: string };
  receipt: { id: string; json_url: string; pdf_url: string };
}

function buildIdentifiers(s: RecordConsentInput["subject"]): { kind: IdentifierKind; value: string }[] {
  const idfs: { kind: IdentifierKind; value: string }[] = [];
  if (s.email) idfs.push({ kind: "EMAIL", value: s.email });
  if (s.rut) idfs.push({ kind: "RUT", value: s.rut });
  if (s.phone) idfs.push({ kind: "PHONE", value: s.phone });
  if (s.cookieId) idfs.push({ kind: "COOKIE_ID", value: s.cookieId });
  if (s.externalId) idfs.push({ kind: "EXTERNAL_ID", value: s.externalId });
  return idfs;
}

export class ConsentClient {
  constructor(private readonly opts: ConsentClientOptions) {}

  async recordConsent(input: RecordConsentInput): Promise<RecordConsentResponse> {
    const identifiers = buildIdentifiers(input.subject);
    if (identifiers.length === 0) {
      throw new Error("at least one identifier required");
    }
    const body = {
      subject: { identifiers, create_if_missing: true },
      purpose_id: input.purposeId,
      consent_method: input.consentMethod,
      language: input.language ?? this.opts.language ?? "es-CL",
      context: input.context,
      policy_version_id: input.policyVersionId,
    };
    return this.post<RecordConsentResponse>("/records", body);
  }

  async withdraw(recordId: string): Promise<unknown> {
    return this.post<unknown>(`/records/${encodeURIComponent(recordId)}/withdraw`, {});
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const url = this.opts.baseUrl.replace(/\/$/, "") + path;
    const res = await fetch(url, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: {
        Authorization: `Bearer ${this.opts.publishableKey}`,
        "Content-Type": "application/json",
        "Idempotency-Key": this.idempotencyKey(),
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`consent_api_error:${res.status}:${text}`);
    }
    return (await res.json()) as T;
  }

  private idempotencyKey(): string {
    const r =
      (typeof globalThis !== "undefined" && (globalThis as any).crypto?.randomUUID?.()) ||
      `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return `idem-${r}`;
  }
}
